/**
 * One CSS declaration, normalized: a property name this module accepted and the
 * value it accepted for it, both trimmed and with nothing dropped in between.
 */
export type StyleDeclaration = { property: string; value: string };

/**
 * Declarations are split on `;` without tracking quotes, so a value containing
 * a semicolon inside a string — `content: "a;b"` — is cut in half and both
 * halves are then rejected as malformed. That is an acceptable trade: the
 * properties worth setting on a run of words do not take string values, and a
 * quote-aware splitter is a second CSS parser to keep correct.
 */
const DECLARATION_SEPARATOR = ';';

/** `--custom-property`, or an ordinary property with an optional vendor prefix. */
const PROPERTY_NAME = /^(--[a-zA-Z0-9-]+|-?[a-zA-Z][a-zA-Z0-9-]*)$/;

/**
 * Properties that do something other than style text, whatever their value.
 *
 * `behavior` and `-moz-binding` attach script to an element through CSS in old
 * engines. They are long dead, and they are named here anyway: the cost is one
 * line, and the alternative is relying on every reader of this file knowing
 * that CSS was once executable.
 */
const REJECTED_PROPERTIES = new Set(['behavior', '-moz-binding']);

/**
 * Constructs that make a declaration do more than describe appearance.
 *
 * `url(` is rejected rather than filtered because every reason to allow it is
 * better served elsewhere: a background image is a component, and a `url()` in
 * authored text is either a tracking pixel, a `data:` payload, or a
 * `javascript:` scheme an older engine will still run. A backslash is rejected
 * for the same reason in one step: CSS escapes let `\75 rl(` spell `url(`, so
 * allowing escapes would mean decoding them before every other check.
 */
const REJECTED_IN_VALUE = [
  'url(',
  'expression(',
  'javascript:',
  'vbscript:',
  '@',
  '\\',
  '<',
  '>',
  '/*',
  '*/',
];

/** Control characters, and the braces that would end a declaration list. */
// eslint-disable-next-line no-control-regex -- control characters are what this matches
const REJECTED_CHARACTERS = /[\u0000-\u001f\u007f{}]/;

/**
 * React drops `!important` from a style object and warns about it, while the
 * string-based ports would honour it — one authored value behaving differently
 * per platform. It comes off everywhere instead, and the class-names field is
 * the supported way to win a specificity fight.
 */
const IMPORTANT_FLAG = /\s*!\s*important\s*$/i;

/** Bounds well past anything typed by hand, so a pasted stylesheet cannot land here. */
const MAX_DECLARATIONS = 40;
const MAX_VALUE_LENGTH = 400;

const isUsableProperty = (property: string): boolean =>
  PROPERTY_NAME.test(property) && !REJECTED_PROPERTIES.has(property.toLowerCase());

const isUsableValue = (value: string): boolean => {
  if (value === '' || value.length > MAX_VALUE_LENGTH) {
    return false;
  }
  if (REJECTED_CHARACTERS.test(value)) {
    return false;
  }
  const lowered = value.toLowerCase();
  return !REJECTED_IN_VALUE.some((rejected) => lowered.includes(rejected));
};

/**
 * A declaration to the pair it means, or `undefined` when it is malformed or
 * carries something this module will not apply.
 */
const parseDeclaration = (declaration: string): StyleDeclaration | undefined => {
  const separator = declaration.indexOf(':');
  if (separator === -1) {
    return undefined;
  }

  // Only the first colon splits: `background: linear-gradient(…)` keeps the rest.
  const property = declaration.slice(0, separator).trim();
  const value = declaration.slice(separator + 1).replace(IMPORTANT_FLAG, '').trim();

  if (!isUsableProperty(property) || !isUsableValue(value)) {
    return undefined;
  }
  return { property, value };
};

/**
 * An authored CSS declaration list to the declarations that will be applied.
 *
 * The list is a denylist over values and an allowlist over property names,
 * which is the same shape as `text-safety/validate-text-content.ts` in the
 * composer and for the same reason: a person writing `color: #c00` should never
 * have to think about this function, while the handful of constructs that turn
 * a declaration into a request or a script are named and refused.
 *
 * A rejected declaration costs only itself. `color: red; behavior: url(x)`
 * still sets the colour, because dropping the whole entry over one bad
 * declaration would lose work the author can see is fine.
 */
export function parseStyleDeclarations(value: unknown): StyleDeclaration[] {
  if (typeof value !== 'string') {
    return [];
  }

  const parsed: StyleDeclaration[] = [];
  const seen = new Set<string>();

  for (const declaration of value.split(DECLARATION_SEPARATOR)) {
    if (declaration.trim() === '') {
      continue;
    }
    const pair = parseDeclaration(declaration);
    if (pair === undefined) {
      continue;
    }
    // Last one wins, as it would in a real declaration list.
    if (seen.has(pair.property)) {
      parsed[parsed.findIndex((existing) => existing.property === pair.property)] = pair;
      continue;
    }
    seen.add(pair.property);
    parsed.push(pair);
    if (parsed.length === MAX_DECLARATIONS) {
      break;
    }
  }

  return parsed;
}

/**
 * Declarations back to a CSS string, for the places that want a `style`
 * attribute rather than an object — the composer's editing surface, and the
 * ports whose templates bind a string.
 */
export function formatStyleDeclarations(declarations: readonly StyleDeclaration[]): string {
  return declarations.map(({ property, value }) => `${property}: ${value}`).join('; ');
}
